import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { QUESTION_TYPES } from '@api/instant_survey';
import { Announcement, Button, GPTIcon, OverviewIcon, toast } from '@utilities';

import {
  getNonInstructionQuestionCountBefore,
  getQuestionsWithScreenersFirst,
} from '../../../../utilities/helpers';
import {
  getPipingQuestionNumbersById,
  replacePipingMentionReferences,
} from '../../utilities/helpers';

import ModalLogicChangeConfirmation from './components/ModalLogicChangeConfirmation';
import Question from './components/Question';
import PreviewBanner from './components/PreviewBanner';
import QuestionPlaceholder from './components/QuestionPlaceholder';
import SequentialMonadicBlock, {
  SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_PREFIX,
  SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_TYPE,
} from './components/SequentialMonadicBlock';
import { SurveyContext } from '../../../../utilities/context';

import rocketError from './assets/rocket-error.svg';

import styles from './_index.module.scss';

const QuestionnaireList = ({
  calculateQuestionNumbers,
  canPreviewSurvey,
  firstErrorRef,
  generationError,
  generationErrorMessage,
  isDetailedQuestionView,
  isVideoSurvey,
  onAcceptPreview,
  onCancelPreview,
  onCreateQuestion,
  onDuplicateQuestion,
  onEditQuestion,
  onOpenAIGenerator,
  onPreviewSurvey,
  onRemoveQuestion,
  onReprompt,
  pipingQuestionsMap,
  questions,
  showActions,
  showPreviewBanner,
}) => {
  const { isReadOnly, questionDisplayIndexById, setShowSurveyTemplateModal, survey, updateSurvey } =
    useContext(SurveyContext);
  const { permissions = {} } = useSelector((state) => state?.user?.data) || {};
  const { canAdvancedEditSurvey, canUseAIGenerationQuestionnaire } = permissions;
  const [questionBeingMoved, setQuestionBeingMoved] = useState(null);
  const [questionsRequiringLogicConfirmation, setQuestionsRequiringLogicConfirmation] =
    useState(null);

  const getSequentialMonadicQuestionIdFromDroppableId = ({ droppableId = '' }) => {
    const prefix = `${SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_PREFIX}-`;
    if (!droppableId.startsWith(prefix)) return null;
    return droppableId.slice(prefix.length);
  };

  const reorderSequentialMonadicSubQuestions = ({ destinationIndex, questionId, sourceIndex }) => {
    const updatedQuestions = survey.questions.map((surveyQuestion) => {
      if (String(surveyQuestion?.id) !== questionId) return surveyQuestion;

      const existingSubQuestions = surveyQuestion.subQuestions || [];
      const activeSubQuestions = existingSubQuestions.filter(({ _delete }) => !_delete);
      const deletedSubQuestions = existingSubQuestions.filter(({ _delete }) => _delete);
      const reorderedSubQuestions = [...activeSubQuestions];
      const [movedSubQuestion] = reorderedSubQuestions.splice(sourceIndex, 1);

      if (!movedSubQuestion) return surveyQuestion;

      reorderedSubQuestions.splice(destinationIndex, 0, movedSubQuestion);

      return {
        ...surveyQuestion,
        subQuestions: [...reorderedSubQuestions, ...deletedSubQuestions],
      };
    });

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
  };

  const onDragEnd = (result) => {
    const destinationIndex = result?.destination?.index;
    const sourceIndex = result?.source?.index;

    if (
      !result?.destination ||
      result.source.droppableId !== result.destination.droppableId ||
      destinationIndex === sourceIndex
    ) {
      return;
    }

    if (result.type === SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_TYPE) {
      const questionId = getSequentialMonadicQuestionIdFromDroppableId({
        droppableId: result.source.droppableId,
      });
      if (!questionId) return;

      reorderSequentialMonadicSubQuestions({
        destinationIndex,
        questionId,
        sourceIndex,
      });
      return;
    }

    let updatedQuestions = [...survey.questions];
    const [questionToMove] = updatedQuestions.splice(sourceIndex, 1);
    updatedQuestions.splice(destinationIndex, 0, questionToMove);
    updatedQuestions = getQuestionsWithScreenersFirst(updatedQuestions);

    // Recalculate question numbers based on new order
    const updatedQuestionNumbers = calculateQuestionNumbers(updatedQuestions);
    const originalQuestionNumbers = calculateQuestionNumbers(survey.questions);
    const updatedPipingQuestionNumbers = getPipingQuestionNumbersById(updatedQuestions);
    const originalPipingQuestionNumbers = getPipingQuestionNumbersById(survey.questions);

    const hasCarryForwardQuestionChanges = updatedQuestions.some(({ id }) => {
      const carryForwardLogic = survey?.carryForwardLogic?.[id];
      if (!carryForwardLogic?.sourceSurveyQuestionId) return false;

      return (
        updatedQuestionNumbers[carryForwardLogic.sourceSurveyQuestionId] >
        updatedQuestionNumbers[id]
      );
    });

    if (hasCarryForwardQuestionChanges) {
      toast.error(
        'Moving this question will break existing carry forward logic. Update or remove the logic before moving this question.'
      );
      return;
    }

    const hasLogicChanges = updatedQuestions.some(({ id }, index) => {
      const currentLogic = survey?.questionLogic[id];

      if (
        // only first two questions (ignoring Instructional Text) can disqualify
        currentLogic?.disqualify &&
        !canAdvancedEditSurvey &&
        getNonInstructionQuestionCountBefore(updatedQuestions, index) >= 2
      ) {
        return true;
      }

      if (
        currentLogic?.skip?.logic.some(
          // skip logic cannot reference questions that are after the current question
          ({ question }) =>
            question &&
            updatedQuestions.findIndex(({ id: questionId }) => question.id === questionId) > index
        )
      ) {
        return true;
      }

      if (currentLogic?.option) {
        const hasInvalidOptionLogic = Object.values(currentLogic.option).some((answerOption) =>
          answerOption?.logic?.some(
            // option logic cannot reference questions that are after the current question
            ({ question }) =>
              question &&
              updatedQuestions.findIndex(({ id: questionId }) => question.id === questionId) > index
          )
        );
        return hasInvalidOptionLogic;
      }

      return false;
    });

    const hasPipingQuestionChanges = updatedQuestions.some(({ id }) => {
      const children = pipingQuestionsMap.childrenByParent[id] || [];
      const hasChildrenAllAfterParent = children.every(
        ({ questionId }) =>
          updatedPipingQuestionNumbers[questionId] > updatedPipingQuestionNumbers[id]
      );
      const parents = pipingQuestionsMap.parentsByChild[id] || [];
      const hasParentsAllBeforeChild = parents.every(
        ({ questionId }) =>
          updatedPipingQuestionNumbers[questionId] < updatedPipingQuestionNumbers[id]
      );

      if (!hasParentsAllBeforeChild || !hasChildrenAllAfterParent) {
        return true;
      }
      return false;
    });

    const questionsWithResyncedPiping = updatedQuestions.map((question) => {
      const parents = pipingQuestionsMap.parentsByChild[question.id] || [];
      if (!parents.length) {
        return question;
      }

      const mentionReferenceMappings = parents.reduce((acc, { questionId: parentId }) => {
        const oldParentCanonicalQuestionNumber = originalPipingQuestionNumbers[parentId];
        const newParentCanonicalQuestionNumber = updatedPipingQuestionNumbers[parentId];
        const oldParentDisplayQuestionNumber = originalQuestionNumbers[parentId];
        const newParentDisplayQuestionNumber = updatedQuestionNumbers[parentId];

        if (
          !oldParentCanonicalQuestionNumber ||
          !newParentCanonicalQuestionNumber ||
          !oldParentDisplayQuestionNumber ||
          !newParentDisplayQuestionNumber
        ) {
          return acc;
        }

        acc.push({
          newCanonicalQuestionNumber: newParentCanonicalQuestionNumber,
          newDisplayQuestionNumber: newParentDisplayQuestionNumber,
          oldCanonicalQuestionNumber: oldParentCanonicalQuestionNumber,
          oldDisplayQuestionNumber: oldParentDisplayQuestionNumber,
        });
        return acc;
      }, []);

      const nextRichText = replacePipingMentionReferences({
        mappings: mentionReferenceMappings,
        value: question.richText,
      });
      const nextText = replacePipingMentionReferences({
        mappings: mentionReferenceMappings,
        value: question.text,
      });

      if (nextRichText === question.richText && nextText === question.text) {
        return question;
      }

      return {
        ...question,
        richText: nextRichText,
        text: nextText,
      };
    });

    if (hasLogicChanges || hasPipingQuestionChanges) {
      setQuestionBeingMoved(questionToMove);
      setQuestionsRequiringLogicConfirmation(questionsWithResyncedPiping);
    } else {
      updateSurvey({ questions: questionsWithResyncedPiping }, { markDirty: true });
    }
  };

  return (
    <>
      <div className={styles['questionnaire-list-container']}>
        {questions.length === 0 &&
          !isVideoSurvey &&
          canUseAIGenerationQuestionnaire &&
          !isReadOnly &&
          !generationError && (
            <Announcement
              className={styles['questionnaire-list-announcement']}
              elevation={1}
              header="Introducing AI Generated Questionnaires"
              icon={<GPTIcon />}
              text={
                <>
                  Writer&apos;s block? Get started with AI suggested questions. Try an{' '}
                  <span
                    className={styles['questionnaire-list-banner-link']}
                    onClick={onOpenAIGenerator}
                  >
                    AI Generated Questionnaire
                  </span>{' '}
                  now.
                </>
              }
              variant="ai"
            />
          )}
        {questions.length === 0 && !generationError && !isReadOnly && (
          <Announcement
            className={styles['questionnaire-list-announcement']}
            elevation={1}
            header="Questionnaire Templates"
            icon={<OverviewIcon />}
            text={
              <>
                Use one of our{' '}
                <span
                  className={styles['questionnaire-list-banner-link']}
                  onClick={() => setShowSurveyTemplateModal(true)}
                >
                  Questionnaire Templates
                </span>{' '}
                to quickly get started.
              </>
            }
            variant="info"
          />
        )}
        {(showPreviewBanner || generationError) && (
          <PreviewBanner
            generationError={generationError}
            generationErrorMessage={generationErrorMessage}
            onAcceptPreview={onAcceptPreview}
            onCancelPreview={onCancelPreview}
            onReprompt={onReprompt}
          />
        )}
        {generationError && !showPreviewBanner && (
          <div className={styles['questionnaire-list-ai-error']}>
            <img alt="Error" src={rocketError} />
            <p className={styles['questionnaire-list-ai-error-message']}>
              Houston, we have a problem
            </p>
            {generationErrorMessage && (
              <p className={styles['questionnaire-list-ai-error-detail']}>
                {generationErrorMessage}
              </p>
            )}
            {onReprompt && <Button onClick={onReprompt} text="Try Again" variant="ai" />}
          </div>
        )}
        {questions.length === 0 && !generationError && !isReadOnly && (
          <QuestionPlaceholder onCreateQuestion={onCreateQuestion} />
        )}
        {questions.length > 0 && !generationError && (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <div
                  className={styles['selections']}
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {questions.map((question, index) => (
                    // Use stable question.id for draggableId to prevent state bugs when reordering questions
                    <Draggable
                      draggableId={`question-${question.id ?? question.questionClientId ?? index}`}
                      index={index}
                      isDragDisabled={isReadOnly}
                      key={`question-${question.id ?? question.questionClientId ?? index}`}
                    >
                      {(provided) => {
                        return question.type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value ? (
                          <Question
                            canPreviewSurvey={canPreviewSurvey}
                            firstErrorRef={firstErrorRef}
                            index={index}
                            isDetailedQuestionView={isDetailedQuestionView}
                            key={`question-${question.id ?? question.questionClientId ?? index}`}
                            // String(index + 1) covers ai generated questions which will not have a calculated display index
                            // this works so long as generated questions are limited to single cost questions
                            number={questionDisplayIndexById[question.id] ?? String(index + 1)}
                            onDuplicateQuestion={onDuplicateQuestion}
                            onEditQuestion={onEditQuestion}
                            onPreviewSurvey={onPreviewSurvey}
                            onRemoveQuestion={onRemoveQuestion}
                            provided={provided}
                            question={question}
                            questionLogic={survey?.questionLogic}
                            selectedQuestions={questions}
                            setQuestionLogic={(questionLogic) =>
                              updateSurvey({ questionLogic }, { markDirty: true })
                            }
                            showActions={showActions}
                          />
                        ) : (
                          <SequentialMonadicBlock
                            canPreviewSurvey={canPreviewSurvey}
                            firstErrorRef={firstErrorRef}
                            isDetailedQuestionView={isDetailedQuestionView}
                            onPreviewSurvey={onPreviewSurvey}
                            onRemoveQuestion={onRemoveQuestion}
                            pipingQuestionsMap={pipingQuestionsMap}
                            provided={provided}
                            question={question}
                            questionLogic={survey?.questionLogic}
                            selectedQuestions={questions}
                            setQuestionLogic={(questionLogic) =>
                              updateSurvey({ questionLogic }, { markDirty: true })
                            }
                            showActions={showActions}
                          />
                        );
                      }}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
      {questionsRequiringLogicConfirmation && (
        <ModalLogicChangeConfirmation
          calculateQuestionNumbers={calculateQuestionNumbers}
          onClose={() => setQuestionsRequiringLogicConfirmation(null)}
          pipingQuestionsMap={pipingQuestionsMap}
          questionBeingMoved={questionBeingMoved}
          questionLogic={survey?.questionLogic}
          questions={questionsRequiringLogicConfirmation}
          updateSurvey={updateSurvey}
        />
      )}
    </>
  );
};

QuestionnaireList.propTypes = {
  calculateQuestionNumbers: PropTypes.func.isRequired,
  canPreviewSurvey: PropTypes.bool.isRequired,
  firstErrorRef: PropTypes.object.isRequired,
  generationError: PropTypes.bool,
  generationErrorMessage: PropTypes.string,
  isDetailedQuestionView: PropTypes.bool.isRequired,
  isVideoSurvey: PropTypes.bool.isRequired,
  onAcceptPreview: PropTypes.func,
  onCancelPreview: PropTypes.func,
  onCreateQuestion: PropTypes.func.isRequired,
  onDuplicateQuestion: PropTypes.func.isRequired,
  onEditQuestion: PropTypes.func.isRequired,
  onOpenAIGenerator: PropTypes.func.isRequired,
  onPreviewSurvey: PropTypes.func.isRequired,
  onRemoveQuestion: PropTypes.func.isRequired,
  onReprompt: PropTypes.func,
  pipingQuestionsMap: PropTypes.object.isRequired,
  questions: PropTypes.array,
  showActions: PropTypes.bool,
  showPreviewBanner: PropTypes.bool,
};

export default QuestionnaireList;
